import React, { useEffect, useImperativeHandle } from 'react';
import { FullscreenIcon, MinimizeIcon } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAritoDialog } from './hooks';
import { cn } from '@/lib/utils';

export interface AritoDialogProps {
  /**
   * Dialog title
   */
  title: string;

  /**
   * Whether the dialog is open (optional, defaults to true)
   */
  open?: boolean;

  /**
   * Callback when the dialog is closed (optional)
   */
  onClose?: () => void;

  /**
   * Dialog content as children
   */
  children: React.ReactNode;

  /**
   * Dialog action buttons
   */
  actions?: React.ReactNode;

  /**
   * Whether to disable closing on backdrop click
   * @default false
   */
  disableBackdropClose?: boolean;

  /**
   * Whether to disable closing on Escape key
   * @default false
   */
  disableEscapeKeyDown?: boolean;

  /**
   * Whether to show fullscreen toggle button
   * @default true
   */
  showFullscreenToggle?: boolean;

  /**
   * Whether the dialog is in fullscreen mode
   * @default false
   */
  fullScreen?: boolean;

  /**
   * Callback when fullscreen mode is toggled
   */
  onFullscreenToggle?: () => void;

  /**
   * Maximum width of the dialog
   * @default 'md'
   */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;

  /**
   * Custom icon to show in the title
   */
  titleIcon?: React.ReactNode;

  classNameContent?: string;

  classNameContainer?: string;

  paperClassName?: string;

  fullScreenPaperClassName?: string;

  zIndex?: number;
}

const maxWidthValueMap: Record<Exclude<AritoDialogProps['maxWidth'], false | undefined>, string> = {
  xs: '444px',
  sm: '600px',
  md: '900px',
  lg: '1200px',
  xl: '1536px'
};

export const AritoDialog = React.forwardRef<{ open: () => void }, AritoDialogProps>(
  (
    {
      title,
      open = true,
      onClose,
      children,
      actions,
      disableBackdropClose = false,
      disableEscapeKeyDown = false,
      showFullscreenToggle = true,
      fullScreen: propFullScreen = false,
      onFullscreenToggle,
      maxWidth = 'md',
      titleIcon,
      classNameContent,
      classNameContainer,
      paperClassName,
      fullScreenPaperClassName,
      zIndex
    },
    ref
  ) => {
    const isMobile = useIsMobile();

    const {
      isOpen,
      setIsOpen,
      isFullScreen,
      setIsFullScreen,
      toggleFullScreen,
      open: openDialog
    } = useAritoDialog({
      initialOpen: open,
      initialFullScreen: propFullScreen,
      onFullscreenToggle
    });

    useEffect(() => {
      setIsFullScreen(propFullScreen);
    }, [propFullScreen, setIsFullScreen]);

    useEffect(() => {
      setIsOpen(open);
    }, [open, setIsOpen]);

    const shouldBeFullScreen = isMobile || isFullScreen;
    useImperativeHandle(
      ref,
      () => ({
        open: openDialog
      }),
      [openDialog]
    );

    const handleOpenChange = (nextOpen: boolean) => {
      if (nextOpen) {
        setIsOpen(true);
        return;
      }

      if (!disableBackdropClose) {
        onClose?.();
      }
    };

    const handleCloseClick = () => {
      if (!disableBackdropClose) {
        onClose?.();
      }
    };

    const maxWidthValue =
      maxWidth === false ? 'calc(100vw - 64px)' : `min(${maxWidthValueMap[maxWidth]}, calc(100vw - 64px))`;
    const contentStyle = {
      ...(zIndex ? { '--arito-dialog-z-index': zIndex, zIndex } : {}),
      maxWidth: shouldBeFullScreen ? undefined : maxWidthValue
    } as React.CSSProperties;
    const overlayStyle = zIndex ? ({ '--arito-dialog-z-index': zIndex, zIndex } as React.CSSProperties) : undefined;

    return (
      <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className={cn('fixed inset-0 z-[var(--arito-dialog-z-index,50)] bg-black/50', classNameContainer)}
            style={overlayStyle}
          />
          <DialogPrimitive.Content
            onEscapeKeyDown={event => {
              if (disableEscapeKeyDown) {
                event.preventDefault();
              }
            }}
            onPointerDownOutside={event => {
              if (disableBackdropClose) {
                event.preventDefault();
              }
            }}
            onInteractOutside={event => {
              if (disableBackdropClose) {
                event.preventDefault();
              }
            }}
            className={cn(
              'fixed left-1/2 top-1/2 z-[var(--arito-dialog-z-index,50)] flex max-h-[90vh] w-fit min-w-0 -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden border border-gray-200 bg-white shadow-lg outline-none',
              shouldBeFullScreen &&
                'inset-0 left-0 top-0 h-screen max-h-screen w-screen max-w-none translate-x-0 translate-y-0',
              shouldBeFullScreen ? fullScreenPaperClassName : paperClassName
            )}
            style={contentStyle}
          >
            <DialogPrimitive.Title className='flex h-[var(--erp-dialog-header-height)] min-h-[var(--erp-dialog-header-height)] items-center justify-between border-b border-gray-300 bg-[#f2f7fc] px-2 py-1 text-black'>
              <div className='flex items-center'>
                {titleIcon}
                <span className={cn('text-[length:var(--erp-dialog-title-size)] font-medium', titleIcon && 'ml-2')}>
                  {title}
                </span>
              </div>

              <div className='ml-auto flex'>
                {showFullscreenToggle && !isMobile && (
                  <button
                    type='button'
                    onClick={toggleFullScreen}
                    title={isFullScreen ? 'Exit fullscreen' : 'Fullscreen'}
                    className='mr-1 inline-flex h-8 w-8 items-center justify-center rounded-sm text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-sky-500'
                  >
                    {isFullScreen && <MinimizeIcon size={18} />}
                    {!isFullScreen && <FullscreenIcon size={18} />}
                  </button>
                )}
                <button
                  type='button'
                  onClick={handleCloseClick}
                  title='Close'
                  className='inline-flex h-8 w-8 items-center justify-center rounded-sm text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-sky-500'
                >
                  <Lineicons aria-hidden='true' icon={XmarkStroke} />
                </button>
              </div>
            </DialogPrimitive.Title>

            <div className={cn('flex min-h-0 flex-1 flex-col overflow-auto p-0', classNameContent)}>{children}</div>

            {actions && (
              <div className='flex h-[43px] min-h-[43px] items-center justify-end gap-1 border-t border-gray-200 bg-[#f2f7fc] px-2 py-1'>
                {actions}
              </div>
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  }
);

AritoDialog.displayName = 'AritoDialog';
